/** Which piece is playing, and what the kernel's events sound like.
 *
 * A component rather than a hook because everything it does needs the
 * `AudioProvider` above it, and the provider is mounted by `App` - a hook
 * called in `App` itself would sit outside its own provider.
 *
 * It renders nothing. Every effect it owns is a subscription that has to be
 * torn down with the game, and a component is the only thing React gives you
 * that has a lifetime.
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";
import type { GameEvent, TownState } from "@game/core";
import type { Scene } from "@game/ui";
import { endCue, useAudioEngine, useButtonClicks, useEventSound, useMusicScene } from "@game/ui";

import type { GameSession } from "./store.js";
import type { AppSnapshot } from "./app.js";
import { trackFor } from "./music.js";
import { viewerNews } from "./journal.js";

const NO_UNSUBSCRIBE = (): void => { /* nothing to unsubscribe from */ };

/**
 * The screen decides the piece, and the order of the tests is the order of what
 * is in front of the player: a battle is over a town screen is over the map.
 */
export function sceneFor(snapshot: AppSnapshot, inBattle: boolean): Scene | null {
  if (snapshot.fixture !== null) return null; // the screenshot harness stays silent
  if (inBattle || snapshot.overlay.kind === "battle") return "battle";
  if (snapshot.screen.kind === "adventure") {
    return snapshot.overlay.kind === "town" ? "town" : "adventure";
  }
  if (snapshot.screen.kind === "menu" || snapshot.screen.kind === "newgame") return "menu";
  return null;
}

/** The faction of the town on screen, which is what picks its theme. */
export function townFaction(snapshot: AppSnapshot, session: GameSession | null): string | null {
  if (snapshot.overlay.kind !== "town" || session === null) return null;
  const town: TownState | undefined = session.getState().towns[snapshot.overlay.town];
  return town?.faction ?? null;
}

export interface GameAudioProps {
  readonly snapshot: AppSnapshot;
  readonly session: GameSession | null;
  readonly inBattle: boolean;
}

export function GameAudio(props: GameAudioProps): null {
  const { snapshot, session, inBattle } = props;
  const engine = useAudioEngine();

  useButtonClicks();
  const scene = sceneFor(snapshot, inBattle);
  useMusicScene(scene, trackFor(scene, townFaction(snapshot, session)));

  // The session's own revision, not the app store's: hundreds of events land
  // inside one computer turn without the app store moving at all.
  const revision = useSyncExternalStore(
    useCallback(
      (l: () => void) => (session === null ? NO_UNSUBSCRIBE : session.subscribe(l)),
      [session],
    ),
    useCallback(() => session?.getVersion() ?? 0, [session]),
  );

  // Only what this seat may know about. The log carries every kingdom's
  // events, so an unfiltered feed sounded the computer's town builds and
  // artifact pickups in the player's ears; `viewerNews` is the same judgement
  // the journal and the toast use.
  const hearable = useCallback((batch: readonly GameEvent[]): readonly GameEvent[] => {
    if (session === null) return batch;
    return viewerNews(batch, session.getState(), session.getViewer());
  }, [session]);
  useEventSound(session?.eventLog() ?? [], revision, true, hearable);

  // The end is sounded here rather than from the `GameOver` event, because the
  // event says who won and only this side knows who was listening. Fired once:
  // `evaluateVictory` runs several times a turn and the flag stays true.
  const sounded = useRef(false);
  useEffect(() => {
    if (session === null) { sounded.current = false; return; }
    const v = session.getState().victory;
    if (!v.over) { sounded.current = false; return; }
    if (sounded.current) return;
    sounded.current = true;
    engine?.play(endCue(v.winner === session.getViewer()));
  }, [engine, session, revision]);

  return null;
}
